/**
 * Formatters for merge token values in renderTemplate.
 * Apply when replacing tokens so output is formatted (currency, date, phone, etc.).
 */

/**
 * Format a number as currency (USD).
 * @param {number|string} value
 * @returns {string}
 */
export function currency(value) {
  if (value == null || value === '') return '';
  const n = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

/**
 * Format a date string or Date for display.
 * @param {string|Date|number} value
 * @param {string} [locale] - e.g. 'en-US'
 * @returns {string}
 */
export function date(value, locale = 'en-US') {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * Format a number as percentage (e.g. 0.11 -> "11%").
 * @param {number|string} value
 * @param {number} [decimals]
 * @returns {string}
 */
export function percent(value, decimals = 0) {
  if (value == null || value === '') return '';
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${Number(n * 100).toFixed(decimals)}%`;
}

/**
 * Format phone number for display (add parentheses/dash if raw digits).
 * @param {string} value
 * @returns {string}
 */
export function phone(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return s;
}

/**
 * Uppercase string.
 * @param {string} value
 * @returns {string}
 */
export function uppercase(value) {
  if (value == null) return '';
  return String(value).toUpperCase();
}

/**
 * Apply formatter by name.
 * @param {string} formatterName - 'currency' | 'date' | 'percent' | 'phone' | 'uppercase'
 * @param {*} value
 * @returns {string}
 */
export function applyFormatter(formatterName, value) {
  if (!formatterName || value == null || value === '') return value == null ? '' : String(value);
  switch (formatterName) {
    case 'currency':
      return currency(value);
    case 'date':
      return date(value);
    case 'percent':
      return percent(value);
    case 'phone':
      return phone(value);
    case 'uppercase':
      return uppercase(value);
    default:
      return String(value);
  }
}
